/**
 * Prop types for the cart page's components, the empty pricing value
 * ({@link defaultPricingData}) that cart and checkout use as "not priced yet", the Items API
 * items-by-CPN request body, and the two checkout buttons ({@link ShoppingCartCheckoutType}).
 */
import React from "react";
import { UOM } from "@ryerson/common.enums";
import {
	Product,
	ShoppingCartPricingResponse,
	Processing,
	ShoppingCartPricingItem,
} from "@ryerson/frontend.common";
import { ShoppingCartBreakdownText } from "./ShoppingCartBreakdownButton/ShoppingCartBreakdownButtonContent";
import { ClearCartContentProps } from "./ShoppingCartBulkActionMenu/ClearCart/ClearCartContent";
import { ShoppingCartSubheaderText } from "./ShoppingCartSubheader/ShoppingCartSubheaderContent";

/** Props `ShoppingCart.tsx` passes to `ShoppingCartDesktop` and `ShoppingCartMobile`. */
export type ShoppingCartProps = {
	/** Removes the selected lines, or every line when none is selected (with undo). */
	clearCart: () => void;
	testingId?: string;
	/** Removes one line by its 0-based row index (with undo). */
	onClickRemoveItemFromCart: (index: number) => void;
	showQuickEntry: boolean;
	setShowQuickEntry: React.Dispatch<React.SetStateAction<boolean>>;
	pricingOfCartItems: ShoppingCartPricingResponse;
	setPricingOfCartItems: React.Dispatch<React.SetStateAction<ShoppingCartPricingResponse>>;
	valueUpdatedIds: any[];
	setValueUpdatedIds: (value: any[]) => void;
	previousPricingData: ShoppingCartPricingResponse;
	/** Reloads the whole cart after quick entry adds lines. */
	quickEntryUpdateShoppingCart: () => void;
	/** The PO number input, scrolled to when it holds ERP-restricted characters at checkout. */
	POInputRef?: React.RefObject<HTMLInputElement>;
};

/**
 * What the cart's check-inventory modal opens with, kept in a ref by `ShoppingCartContext.tsx`.
 * `index` is a signed-in user's line id or a guest's 0-based row index.
 */
export type InventoryModalProps = {
	index: number;
	product: Product;
	quantity: number;
	quantityUom: UOM;
	priceUom: UOM;
	availablePlant: number[];
};

/**
 * What the cart's define-cut and remove-cut modals open with, kept in a ref by
 * `ShoppingCartContext.tsx`. `index` as in {@link InventoryModalProps}; `isDelete` is not set
 * by the context, which picks the modal from its own argument.
 */
export type DefineCutCartModalProps = {
	index: number;
	product: Product;
	definedCutData: Processing;
	isDelete?: boolean;
};

/** Props of `ShoppingCart.tsx`. */
export type ShoppingCartType = {
	testingId?: string;
};

/**
 * Empty pricing: blank amounts and no lines. Cart and checkout set it before re-pricing, and
 * read an empty `subtotal` as "not priced yet": `ShoppingCartHeader.tsx` shows a dash for the
 * total, and `ShoppingCartContext.tsx` → `setPricingForCartData` applies the next response.
 * One shared object: code that sets it as state must not change it in place.
 */
export const defaultPricingData: ShoppingCartPricingResponse = {
	fuelSurcharge: "",
	energySurcharge: "",
	taxAmount: "",
	subtotal: "",
	totalWeight: "",
	items: [] as ShoppingCartPricingItem[],
};

/** Props of `ShoppingCartSubheader/ShoppingCartSubheaderDesktop.tsx`. */
export type ShoppingCartSubheaderType = {
	dataTestIdCustomerPickup?: string;
	content: ShoppingCartSubheaderText;
	setShowQuickEntry: (value: boolean) => void;
	showQuickEntry: boolean;
	isMobile?: boolean;
	setShowBulkUpload: React.Dispatch<React.SetStateAction<boolean>>;
	isValidZipCode: boolean;
};

/** Props of `ShoppingCartSubheader/ShoppingCartSubheaderMobile.tsx`. */
export type ShoppingCartSubheaderMobileType = {
	dataTestIdCustomerPickup?: string;
	isMobile?: boolean;
};

/** Props of `ShoppingCartSubheader/ShoppingCartSubheader.tsx`. */
export type ShoppingCartSubheaderProps = {
	testingId?: string;
	setShowQuickEntry: (value: boolean) => void;
	showQuickEntry: boolean;
};

/** Props of `ShoppingCartHeader/ShoppingCartHeader.tsx`. */
export type ShoppingHeaderProps = {
	clearCart: () => void;
	cartItemCount: number;
	cartData: any;
	setShowQuickEntry: (value: boolean) => void;
	showQuickEntry: boolean;
	pricingOfCartItems: ShoppingCartPricingResponse;
	/** Adds the "estimated" label to the total; both cart views pass true. */
	displayEstimated?: boolean;
};

/** Props of `ShoppingCartEmptyAlert/ShoppingCartEmptyAlert.tsx`. */
export type EmptyCartAlertProps = {
	isMobile: boolean;
	showQuickEntry: boolean;
};

/** Props of `ShoppingCartBulkActionMenu/ClearCart/ClearCart.tsx`. */
export type ClearCartProps = {
	cartData: any[];
	clearCart: () => void;
	isClearCartModalOpen: boolean;
	setClearCartModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

/** Props of the clear-cart views (`ClearCart/ClearCartDesktop.tsx`, `ClearCartMobile.tsx`). */
export type ClearCartType = {
	cartData: any[];
	clearCart: () => void;
	content: ClearCartContentProps;
	isClearCartModalOpen: boolean;
	setClearCartModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

/** Props of `ShoppingCartBreakdownButton/ShoppingCartBreakdownButton.tsx`. */
export type ShoppingCartButtonComponentProps = {
	cartItemCount: number;
};

/** Props of the checkout-button views under `ShoppingCartBreakdownButton/`. */
export type ShoppingCartButtonComponentType = {
	content: ShoppingCartBreakdownText;
	loggedIn: boolean;
	cartItemCount: number;
	isMissingRequiredCut: boolean;
	isMissingTaxes: boolean;
};

/** One SKU and the customer part number it is ordered under (the cart sends `""` for none). */
export type ByCPNItem = { skuNumber: string; cpn: string };
/**
 * Body of the Items API items-by-SKU-and-CPN request (`APIUrl.ITEM_DETAILS_BY_CPN`), also used
 * by the checkout pages. The cart builds it with `Helper/shoppingCartHelper.ts` →
 * `createRequestBodyForItemsByCPN`, which sends `zipCode` only for a guest or a user with no
 * customer number.
 */
export interface ItemsByCPNRequestBody {
	items: Array<ByCPNItem>;
	zipCode?: string;
}

/**
 * The checkout button clicked on the cart page, stored in the context's
 * `checkoutTypeSubmitted`. The values match the checkout page paths (`/checkout/express`,
 * `/checkout/shipping-billing`), though the context navigates with literal paths.
 */
export enum ShoppingCartCheckoutType {
	ExpressCheckout = "express",
	ProceedToCheckout = "shipping-billing",
}
